import { get } from "lodash";
import { Check, X } from "lucide-react";
import { memo, useCallback } from "react";
import { useLocation } from "wouter";

import { type OrderContinueRebiddingResponseDTO } from "@/common/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
} from "@/common/components";
import { ROUTES } from "@/common/config";
import { formatDatetime, formatDate } from "@/common/lib";

interface ExtensionRebiddingTableProps {
  requests: OrderContinueRebiddingResponseDTO[];
  isLoading: boolean;
  isPending?: boolean;
  onApprove?: (requestId: string) => void;
  onCancel?: (requestId: string) => void;
}

export const ExtensionRebiddingTable = memo(function ExtensionRebiddingTable({
  requests,
  isLoading,
  isPending,
  onApprove,
  onCancel,
}: ExtensionRebiddingTableProps) {
  const [, setLocation] = useLocation();

  const handleRowClick = useCallback(
    (requestId: string) => {
      setLocation(ROUTES.ORDER_ID.replace(":id", requestId));
    },
    [setLocation],
  );

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="caption">Загрузка запросов на продление переторжки...</p>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="caption">Запросы на продление переторжки отсутствуют</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Номер заявки</TableHead>
          <TableHead className="w-[200px]">Количество дней переторжки</TableHead>
          <TableHead>Текущий срок</TableHead>
          <TableHead>Дата создания заявки</TableHead>
          <TableHead>Действия</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {requests.map((request) => (
          <TableRow
            key={request.id}
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(get(request, "orderId", ""));
            }}
          >
            <TableCell className="font-medium">{request.orderNumber}</TableCell>
            <TableCell className="w-[200px]">{request.dayCount}</TableCell>
            <TableCell>
              <>
                <p className="text-sm text-nowrap">с {formatDatetime(request.tradeBegin)}</p>
                <p className="text-sm text-nowrap">по {formatDatetime(request.tradeEnd)}</p>
              </>
            </TableCell>
            <TableCell>{formatDate(request.orderCreation)}</TableCell>
            <TableCell className="text-center">
              <div className="flex items-center justify-center gap-2">
                {request.id && (
                  <>
                    <Button
                      size="sm"
                      className="btn-primary"
                      disabled={isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        onApprove?.(request.id!);
                      }}
                    >
                      <Check className="h-4 w-4 mr-1" />
                      <span className="hidden md:block">Утвердить</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:text-white"
                      disabled={isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        onCancel?.(request.id!);
                      }}
                    >
                      <X className="h-4 w-4 mr-1" />
                      <span className="hidden md:block">Отклонить</span>
                    </Button>
                  </>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
});
